import { useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Col, Form, Row } from 'antd';
import { getFilter, useFilter } from 'hooks/useFilter';
import { useRecoilValue } from 'recoil';
import { useApi } from 'hooks/useApi';
import { useNavigate } from 'hooks/useNavigate';
import ErrorHelper from 'helpers/ErrorHelper';
import { Link } from 'react-router-dom';
import Button from 'components/button';
import { EyeOutlined } from '@ant-design/icons';
import { DeleteIcon, EditIcon } from 'icons';
import { getUser } from 'hooks/useUser';
import CompaniesApi from 'libs/api/companies';
import LinkWithParams from '../../../../components/linkWithParams';

const api = new CompaniesApi();

const useCompanies = ({ isMainPage = true, isEdit } = {}) => {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const filterData = useRecoilValue(getFilter);
  const user = useRecoilValue(getUser);
  const { t } = useTranslation();
  const { handleGetFilters } = useFilter();
  const {
    singleItem,
    data,
    loading,
    onChangeTableFilter,
    handleDownloadExcel,
    onDelete,
    updateInProcess,
    setUpdateInProcess,
  } = useApi({
    resourceKey: 'companies',
    singleResourceKey: 'company',
    api,
    isMainPage,
  });

  const columns = [
    {
      title: t('Azioni'),
      dataIndex: '',
      render: (data, row) => (
        <Row
          gutter={16}
          style={{ minWidth: 150 }}
          justify={'left'}
          onClick={(e) => e.stopPropagation()}
        >
          <Col>
            <LinkWithParams to={`${row.id}/view`}>
              <Button type={'primary-white'} icon={<EyeOutlined />} />
            </LinkWithParams>
          </Col>
          <Col>
            <Link to={`${row.id}/edit`}>
              <Button icon={<EditIcon />} />
            </Link>
          </Col>
          <Col>
            <LinkWithParams to={`${row.id}/delete`}>
              <Button icon={<DeleteIcon />} />
            </LinkWithParams>
          </Col>
        </Row>
      ),
    },
    {
      title: t('Ragione sociale'),
      dataIndex: 'title',
    },
    {
      title: t('Codice Ateco'),
      dataIndex: 'ateco_code',
    },
    {
      title: t('Partita IVA'),
      dataIndex: 'tax_code',
    },
    {
      title: t('Codice SDI'),
      dataIndex: 'invoice_code',
    },
    {
      title: t('Sede legale'),
      dataIndex: 'address',
    },
    {
      title: t('Stripe ID'),
      dataIndex: 'stripe_id',
    },
    {
      title: t('Insegne'),
      dataIndex: 'shops',
      render: (data) => data.map((i) => i.title).join(','),
    },
  ];

  const filters = useMemo(() => {
    return [
      {
        title: t('Cerca società'),
        type: 'input',
        name: 'by_text',
        values: [],
        allowClear: true,
      },
    ];
  }, [t]);

  const handleCreateOrUpdate = useCallback(
    async (values, id) => {
      setUpdateInProcess(true);
      try {
        const data = {
          company: {
            ...values,
            user_id: user.id,
          },
        };
        if (!id) {
          await api.create({ data: { ...data, lombardy: true } });
        } else {
          await api.put(id, { data: { ...data, lombardy: true } });
        }
        handleGetFilters();
        navigate('/profile/companies/list');
      } catch (e) {
        form.setFields(ErrorHelper.getErrorsForForm(e?.response?.data || {}));
      } finally {
        setUpdateInProcess(false);
      }
    },
    [user, form, navigate, setUpdateInProcess],
  );

  useEffect(() => {
    if (isEdit) {
      let fieldsValues = [];
      for (let key in singleItem) {
        switch (key) {
          case 'shops': {
            fieldsValues.push({
              name: 'shop_ids',
              value: singleItem[key]?.map((i) => i.id),
            });
            break;
          }
          default: {
            fieldsValues.push({
              name: key,
              value: singleItem[key],
            });
          }
        }
      }
      form.setFields(fieldsValues);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [singleItem, isEdit]);

  const handleDeleteCompany = async (...rest) => {
    await onDelete(...rest);
    handleGetFilters();
  };

  return {
    handleCreateOrUpdate,
    form,
    columns,
    filters,
    filterData,
    updateInProcess,
    handleDownloadExcel,
    data,
    loading,
    onChangeTableFilter,
    onDelete: handleDeleteCompany,
    singleItem,
  };
};

export default useCompanies;
